/**
 * Discussion Thread Component
 * Displays a discussion with nested posts/replies
 */

import { useState, useEffect, useMemo, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Popover,
  InputAdornment,
} from '@mui/material'
import { Image as ImageIcon, Close as CloseIcon, CameraAlt as CameraIcon, EmojiEmotions as EmojiIcon } from '@mui/icons-material'
import { socialApi } from '@api/endpoints/social'
import { PostDto, CreatePostRequest, FlagPostRequest, DiscussionDto } from '@api/types/social'
import { DiscussionPost } from './DiscussionPost'
import '../styles/discussions.css'

interface DiscussionThreadProps {
  /** Either supply `discussionId` plus the parent context, or pass the full `discussion` directly. */
  discussionId?: string
  discussion?: DiscussionDto
  /** Owning group (only set for group-scoped threads). */
  groupId?: string
  /** Owning adventure (only set for adventure-scoped threads). */
  adventureId?: string
  highlightedPostId?: string
  canReply?: boolean // Whether the user can reply to posts (default: true for backward compatibility)
  isModerator?: boolean
  sortBy?: PostSortOption
  onSortChange?: (sortBy: PostSortOption) => void
  onError?: (error: Error) => void
  /** Optional handler to dismiss the thread view (used by ProfileBlogTab's drill-down). */
  onBack?: () => void
}

interface PostNode extends PostDto {
  children: PostNode[]
}

// Sorting options for posts
type PostSortOption = 'oldest' | 'newest' | 'activity'

// Popular emojis - curated list for quick access without scrolling
const POPULAR_EMOJIS = [
  '😀', '😃', '😄', '😁', '😅', '🤣', '😂', '🙂', '😉', '😊', '😍', '🥰', '😘', '😋', '😎', '🤔', '🤨', '😐', '😑', '😶', '🙄', '😬', '😌', '😔', '😪', '😴', '🤤', '😷', '🤒', '🤕', '🤢', '🤮', '🥵', '🥶', '😵', '🤯', '🥳', '😕', '😟', '🙁', '☹️', '😮', '😲', '😳', '🥺', '😢', '😭', '😱', '😖', '😣', '😞', '😓', '😩', '😫', '😤', '😡', '😠', '💀', '💩', '🤡', '👻', '👽', '🤖',
  '👋', '🤚', '✋', '🖖', '👌', '🤌', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '👇', '☝️', '👍', '👎', '✊', '👊', '👏', '🙌', '👐', '🤝', '🙏', '💪', '👀', '👁️', '👅', '👄', '💋',
  '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '💔', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '✨', '⭐', '🌟', '💫', '🔥', '💥', '💯', '✅', '❌',
  '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯', '🦁', '🐮', '🐷', '🐸', '🐵', '🙈', '🙉', '🙊', '🐔', '🐧', '🐦', '🐤', '🦆', '🦅', '🦉', '🐝', '🦋', '🐌', '🐞', '🐢', '🐍', '🦎', '🦖', '🐙', '🦑', '🐠', '🐟', '🐬', '🐳', '🐋', '🦈', '🐊', '🐅', '🐆', '🦓', '🦍', '🐘', '🦛', '🦏', '🦒', '🐃', '🐂', '🐄', '🐎', '🐖', '🐑', '🐐', '🐕', '🐩', '🐈',
  '🌵', '🎄', '🌲', '🌳', '🌴', '🌱', '🌿', '☘️', '🍀', '🍂', '🍁', '🍄', '🌾', '💐', '🌷', '🌹', '🌺', '🌸', '🌼', '🌻', '🌞', '🌝', '🌙', '🌎', '🌍', '🌏', '⭐', '🌟', '✨', '⚡', '☄️', '💥', '🔥', '🌈', '☀️', '⛅', '☁️', '🌧️', '⛈️', '❄️', '☃️', '💨', '💧', '💦', '☔', '🌊',
  '🍏', '🍎', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓', '🍈', '🍒', '🍑', '🥭', '🍍', '🥥', '🥝', '🍅', '🥑', '🥦', '🥒', '🌽', '🥕', '🥔', '🥐', '🍞', '🥖', '🧀', '🥚', '🍳', '🥞', '🥓', '🍗', '🍖', '🌭', '🍔', '🍟', '🍕', '🥪', '🌮', '🌯', '🥗', '🍝', '🍜', '🍲', '🍛', '🍣', '🍱', '🥟', '🍤', '🍙', '🍚', '🍘', '🍥', '🍢', '🍡', '🍧', '🍨', '🍦', '🥧', '🧁', '🍰', '🎂', '🍮', '🍭', '🍬', '🍫', '🍿', '🍩', '🍪', '🍯', '🥛', '☕', '🍵', '🥤', '🍶', '🍺', '🍻', '🥂', '🍷', '🥃', '🍸', '🍹', '🍾',
  '⚽', '🏀', '🏈', '⚾', '🎾', '🏐', '🏉', '🎱', '🏓', '🏸', '🥊', '🥋', '🛹', '⛸️', '🎿', '🏂', '🏋️', '🤸', '⛹️', '🤾', '🏌️', '🏇', '🧘', '🏊', '🏄', '🚣', '🧗', '🚵', '🚴', '🏆', '🥇', '🥈', '🥉', '🏅', '🎖️', '🎗️', '🎫', '🎟️', '🎪', '🎭', '🎨', '🎬', '🎤', '🎧', '🎼', '🎹', '🎸', '🎻', '🎲', '🎯', '🎳', '🎮', '🎰',
  '🚗', '🚕', '🚙', '🚌', '🚎', '🏎️', '🚓', '🚑', '🚒', '🚐', '🚚', '🚛', '🚜', '🛴', '🚲', '🛵', '🏍️', '✈️', '🛫', '🛬', '🚀', '🛸', '🚁', '🛶', '⛵', '🚤', '🛥️', '⛴️', '🚢', '🏰', '🏯', '🗿', '🗽', '🗼', '🎡', '🎢', '🎠', '⛲', '🏖️', '🏝️', '🏔️', '⛰️', '🌋', '🗻', '🏕️', '⛺', '🏠', '🏡', '🏢', '🏬', '🏣', '🏤', '🏥', '🏦', '🏨', '🏪', '🏫', '💒', '⛪', '🕌',
  '⌚', '📱', '📲', '💻', '⌨️', '🖥️', '🖨️', '🖱️', '💽', '💾', '💿', '📀', '📷', '📸', '📹', '🎥', '📞', '☎️', '📺', '📻', '🎙️', '⏰', '⏱️', '⏲️', '🕰️', '⌛', '⏳', '🔋', '🔌', '💡', '🔦', '🕯️', '💸', '💵', '💴', '💶', '💷', '💰', '💳', '💎', '🔨', '🔧', '🔩', '⚙️', '🔫', '💣', '🔪', '🗡️', '⚔️', '🛡️', '🔮', '💊', '💉', '🧬', '🔬', '🔭', '📡', '🚪', '🛏️', '🛋️', '🚽', '🚿', '🛁', '🧸', '🎁', '🎈', '🎀', '🎊', '🎉', '🎎', '🏮', '🎐', '✉️', '📧', '💌', '📦', '📋', '📁', '📂', '📰', '📓', '📔', '📒', '📕', '📗', '📘', '📙', '📚', '📖', '🔖', '📎', '📐', '📏', '📌', '📍', '✂️', '🖊️', '✏️', '🔍', '🔎', '🔒', '🔓',
  '👁️‍🗨️', '💬', '💭', '🗯️', '♠️', '♣️', '♥️', '♦️', '🎴', '🎭', '🖼️', '🎨', '🧵', '🧶', '🎼', '🎵', '🎶', '☮️', '✝️', '☪️', '🕉️', '☸️', '✡️', '🔯', '🕎', '☯️', '🆔', '⚛️', '❗', '❓', '❔', '‼️', '⁉️', '⚠️', '🔱', '⚜️', '♻️', '🌐', '💠', '🌀', '💤', '🏧', '🚾', '♿', '🅿️', '🚹', '🚺', '🚼', '🚻', '🚮', '🎦', '📶', '🔣', 'ℹ️', '🆗', '🆙', '🆒', '🆕', '🆓', '0️⃣', '1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣', '6️⃣', '7️⃣', '8️⃣', '9️⃣', '🔟', '💯', '🔢', '#️⃣', '*️⃣', '▶️', '⏸️', '⏹️', '⏺️', '⏭️', '⏮️', '⏩', '⏪', '⏫', '⏬', '◀️', '🔼', '🔽', '➡️', '⬅️', '⬆️', '⬇️', '↗️', '↘️', '↙️', '↖️', '↕️', '↔️', '🔀', '🔁', '🔂', '🔄', '🔃', '➕', '➖', '➗', '✖️', '♾️', '💲', '™️', '©️', '®️', '🔚', '🔙', '🔛', '🔝', '🔜', '☑️', '🔘', '🔴', '🟠', '🟡', '🟢', '🔵', '🟣', '⚫', '⚪', '🟤', '🔺', '🔻', '🔸', '🔹', '🔶', '🔷', '🔳', '🔲', '▪️', '▫️', '🟥', '🟧', '🟨', '🟩', '🟦', '🟪', '⬛', '⬜', '🟫', '🔈', '🔇', '🔉', '🔊', '🔔', '🔕', '📣', '📢',
  '🏁', '🚩', '🎌', '🏴', '🏳️', '🏳️‍🌈', '🏳️‍⚧️', '🇺🇸', '🇬🇧', '🇨🇦', '🇦🇺', '🇩🇪', '🇫🇷', '🇮🇹', '🇪🇸', '🇯🇵', '🇰🇷', '🇨🇳', '🇮🇳', '🇧🇷', '🇲🇽', '🇷🇺', '🇿🇦',
]

export function DiscussionThread({
  discussionId,
  groupId,
  highlightedPostId,
  canReply = true,
  isModerator = false,
  sortBy = 'oldest',
  onSortChange,
  onError
}: DiscussionThreadProps) {
  const queryClient = useQueryClient()
  const [newPostContent, setNewPostContent] = useState('')
  const [isReplyFocused, setIsReplyFocused] = useState(false)
  const [selectedImage, setSelectedImage] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [uploadingImage, setUploadingImage] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const [emojiAnchorEl, setEmojiAnchorEl] = useState<HTMLElement | null>(null)
  const [emojiSearch, setEmojiSearch] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const textFieldRef = useRef<HTMLInputElement>(null)

  // Fetch posts for this discussion
  const {
    data: posts,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['discussion-posts', discussionId],
    queryFn: () => socialApi.getDiscussionPosts(discussionId),
    refetchOnWindowFocus: false,
  })

  // Create post mutation
  const createPostMutation = useMutation({
    mutationFn: (request: CreatePostRequest) => socialApi.createPost(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discussion-posts', discussionId] })
      queryClient.invalidateQueries({ queryKey: ['discussions'] })
      setNewPostContent('')
    },
    onError: (err) => {
      onError?.(err as Error)
    },
  })

  // Update post mutation
  const updatePostMutation = useMutation({
    mutationFn: ({ postId, content }: { postId: string; content: string }) =>
      socialApi.updatePost(postId, { content }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discussion-posts', discussionId] })
    },
    onError: (err) => {
      onError?.(err as Error)
    },
  })

  // Delete post mutation
  const deletePostMutation = useMutation({
    mutationFn: (postId: string) => socialApi.deletePost(postId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discussion-posts', discussionId] })
      queryClient.invalidateQueries({ queryKey: ['discussions'] })
    },
    onError: (err) => {
      onError?.(err as Error)
    },
  })

  // Flag post mutation
  const flagPostMutation = useMutation({
    mutationFn: ({ postId, request }: { postId: string; request: FlagPostRequest }) =>
      socialApi.flagPost(postId, request),
    onSuccess: () => {
      alert('Post reported to moderators')
    },
    onError: (err) => {
      onError?.(err as Error)
    },
  })

  // Hide post mutation
  const hidePostMutation = useMutation({
    mutationFn: ({ postId, reason }: { postId: string; reason: string }) =>
      socialApi.hidePost(postId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discussion-posts', discussionId] })
    },
    onError: (err) => {
      onError?.(err as Error)
      alert('Failed to hide post. Please try again.')
    },
  })

  // Unhide post mutation
  const unhidePostMutation = useMutation({
    mutationFn: (postId: string) => socialApi.unhidePost(postId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discussion-posts', discussionId] })
    },
    onError: (err) => {
      onError?.(err as Error)
      alert('Failed to unhide post. Please try again.')
    },
  })

  // Build a tree structure from flat posts array
  const postTree = useMemo(() => {
    if (!posts) return []

    const postMap = new Map<string, PostNode>()
    const rootPosts: PostNode[] = []

    // Initialize all posts with empty children arrays
    posts.forEach((post) => {
      postMap.set(post.id, { ...post, children: [] })
    })

    // Build the tree
    posts.forEach((post) => {
      const node = postMap.get(post.id)!
      if (post.parentPostId) {
        const parent = postMap.get(post.parentPostId)
        if (parent) {
          parent.children.push(node)
        } else {
          // Parent not found, treat as root
          rootPosts.push(node)
        }
      } else {
        rootPosts.push(node)
      }
    })

    // Helper to get the most recent activity date for a post (including all descendants)
    const getMostRecentActivity = (node: PostNode): number => {
      let mostRecent = new Date(node.createdDate).getTime()
      node.children.forEach(child => {
        const childRecent = getMostRecentActivity(child)
        if (childRecent > mostRecent) {
          mostRecent = childRecent
        }
      })
      return mostRecent
    }

    // Helper to get total descendant count
    const getTotalDescendants = (node: PostNode): number => {
      let count = node.children.length
      node.children.forEach(child => {
        count += getTotalDescendants(child)
      })
      return count
    }

    // Sort function based on selected option
    const getSortFunction = () => {
      switch (sortBy) {
        case 'newest':
          return (a: PostNode, b: PostNode) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
        case 'activity':
          return (a: PostNode, b: PostNode) => {
            // Sort by activity: combination of reply count and most recent activity
            const aActivity = getMostRecentActivity(a)
            const bActivity = getMostRecentActivity(b)
            const aReplies = getTotalDescendants(a)
            const bReplies = getTotalDescendants(b)

            // Prioritize posts with more replies, then by most recent activity
            if (aReplies !== bReplies) {
              return bReplies - aReplies
            }
            return bActivity - aActivity
          }
        case 'oldest':
        default:
          return (a: PostNode, b: PostNode) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime()
      }
    }

    const sortFunction = getSortFunction()

    // Sort children recursively
    const sortChildren = (node: PostNode) => {
      node.children.sort(sortFunction)
      node.children.forEach(sortChildren)
    }
    rootPosts.forEach(sortChildren)

    // Sort root posts
    return rootPosts.sort(sortFunction)
  }, [posts, sortBy])

  const handleCreateReply = async (parentPostId: string, content: string, imageId?: string) => {
    await createPostMutation.mutateAsync({
      discussionId,
      parentPostId,
      content,
      imageId,
    })
  }

  const handleCreateTopLevelPost = async () => {
    if (!newPostContent.trim()) return

    let imageId: string | undefined

    // Upload image first if one is selected
    if (selectedImage) {
      setUploadingImage(true)
      try {
        const response = await socialApi.uploadDiscussionImage(selectedImage)
        imageId = response.imageId
      } catch (err) {
        onError?.(err as Error)
        setUploadingImage(false)
        return
      }
      setUploadingImage(false)
    }

    await createPostMutation.mutateAsync({
      discussionId,
      content: newPostContent,
      imageId,
    })

    // Clear image after successful post
    setSelectedImage(null)
    setImagePreview(null)
  }

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      onError?.(new Error('Please select an image file'))
      return
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      onError?.(new Error('Image must be less than 5MB'))
      return
    }

    setSelectedImage(file)

    // Create preview
    const reader = new FileReader()
    reader.onloadend = () => {
      setImagePreview(reader.result as string)
    }
    reader.readAsDataURL(file)
  }

  const handleRemoveImage = () => {
    setSelectedImage(null)
    setImagePreview(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const processImageFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB')
      return
    }
    setSelectedImage(file)
    const reader = new FileReader()
    reader.onloadend = () => {
      setImagePreview(reader.result as string)
    }
    reader.readAsDataURL(file)
  }

  const handlePaste = (event: React.ClipboardEvent) => {
    const items = event.clipboardData?.items
    if (!items) return

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile()
        if (file) {
          event.preventDefault()
          processImageFile(file)
          break
        }
      }
    }
  }

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragOver(true)
  }

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragOver(false)
  }

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragOver(false)

    const files = event.dataTransfer?.files
    if (files && files.length > 0) {
      const file = files[0]
      if (file.type.startsWith('image/')) {
        processImageFile(file)
      }
    }
  }

  const handleEditPost = async (postId: string, content: string) => {
    await updatePostMutation.mutateAsync({ postId, content })
  }

  const handleDeletePost = async (postId: string) => {
    await deletePostMutation.mutateAsync(postId)
  }

  const handleFlagPost = async (postId: string, request: FlagPostRequest) => {
    await flagPostMutation.mutateAsync({ postId, request })
  }

  const handleHidePost = async (postId: string, reason: string) => {
    await hidePostMutation.mutateAsync({ postId, reason })
  }

  const handleUnhidePost = async (postId: string) => {
    await unhidePostMutation.mutateAsync(postId)
  }

  const handleEmojiClick = (event: React.MouseEvent<HTMLElement>) => {
    setEmojiAnchorEl(event.currentTarget)
  }

  const handleEmojiClose = () => {
    setEmojiAnchorEl(null)
    setEmojiSearch('')
  }

  const handleEmojiSelect = (emoji: string) => {
    // Insert emoji at cursor position
    const textarea = textFieldRef.current?.querySelector('textarea') || textFieldRef.current?.querySelector('input')
    if (textarea) {
      const start = (textarea as HTMLTextAreaElement).selectionStart || newPostContent.length
      const end = (textarea as HTMLTextAreaElement).selectionEnd || newPostContent.length
      const newText = newPostContent.substring(0, start) + emoji + newPostContent.substring(end)
      setNewPostContent(newText)

      // Set cursor position after emoji
      setTimeout(() => {
        (textarea as HTMLTextAreaElement).selectionStart = (textarea as HTMLTextAreaElement).selectionEnd = start + emoji.length
        textarea.focus()
      }, 0)
    } else {
      // Fallback: just append to end
      setNewPostContent(prev => prev + emoji)
    }
  }

  // Emoji keyword mapping for search
  const getEmojiKeywords = (emoji: string): string[] => {
    const keywordMap: Record<string, string[]> = {
      // Smileys
      '😀': ['smile', 'happy', 'grin', 'face'],
      '😃': ['smile', 'happy', 'grin', 'joy'],
      '😄': ['smile', 'happy', 'laugh', 'joy'],
      '😁': ['smile', 'happy', 'grin', 'teeth'],
      '😅': ['smile', 'sweat', 'laugh', 'nervous'],
      '🤣': ['laugh', 'rofl', 'rolling', 'funny'],
      '😂': ['laugh', 'cry', 'tears', 'joy', 'funny'],
      '🙂': ['smile', 'happy', 'simple'],
      '😉': ['wink', 'flirt', 'joke'],
      '😊': ['smile', 'blush', 'happy', 'warm'],
      '😍': ['love', 'heart', 'eyes', 'crush'],
      '🥰': ['love', 'hearts', 'happy', 'warm'],
      '😘': ['kiss', 'love', 'blow'],
      '😋': ['yum', 'tongue', 'delicious', 'food'],
      '😎': ['cool', 'sunglasses', 'awesome'],
      '🤔': ['think', 'hmm', 'consider', 'wonder'],
      '🤨': ['raised', 'eyebrow', 'skeptical', 'suspicious'],
      '😐': ['neutral', 'blank', 'meh'],
      '😑': ['expressionless', 'blank', 'meh'],
      '😶': ['silent', 'quiet', 'no mouth'],
      '🙄': ['eye roll', 'annoyed', 'whatever'],
      '😬': ['grimace', 'awkward', 'nervous'],
      '😌': ['relieved', 'peaceful', 'content'],
      '😔': ['sad', 'pensive', 'down'],
      '😪': ['sleepy', 'tired', 'sleep'],
      '😴': ['sleep', 'zzz', 'tired'],
      '🤤': ['drool', 'hungry', 'yum'],
      '😷': ['mask', 'sick', 'medical', 'covid'],
      '🤒': ['sick', 'fever', 'thermometer', 'ill'],
      '🤕': ['hurt', 'injured', 'bandage'],
      '🤢': ['sick', 'nauseous', 'ill', 'gross'],
      '🤮': ['vomit', 'puke', 'sick', 'gross'],
      '🥵': ['hot', 'sweat', 'heat'],
      '🥶': ['cold', 'freeze', 'frozen'],
      '😵': ['dizzy', 'confused', 'knocked out'],
      '🤯': ['mind blown', 'explode', 'shocked', 'amazed'],
      '🥳': ['party', 'celebrate', 'birthday', 'fun'],
      '😕': ['confused', 'uncertain', 'unsure'],
      '😟': ['worried', 'concerned', 'anxious'],
      '🙁': ['sad', 'frown', 'unhappy'],
      '☹️': ['sad', 'frown', 'unhappy'],
      '😮': ['wow', 'surprised', 'gasp'],
      '😲': ['shocked', 'surprised', 'astonished'],
      '😳': ['flushed', 'embarrassed', 'blush'],
      '🥺': ['pleading', 'puppy eyes', 'beg', 'cute'],
      '😢': ['cry', 'sad', 'tears'],
      '😭': ['cry', 'sobbing', 'tears', 'sad'],
      '😱': ['scream', 'scared', 'fear', 'shock'],
      '😖': ['confounded', 'frustrated', 'upset'],
      '😣': ['persevere', 'struggle', 'pain'],
      '😞': ['disappointed', 'sad', 'let down'],
      '😓': ['sweat', 'stress', 'anxious'],
      '😩': ['weary', 'tired', 'exhausted'],
      '😫': ['tired', 'exhausted', 'fed up'],
      '😤': ['triumph', 'proud', 'huff'],
      '😡': ['angry', 'mad', 'rage', 'furious'],
      '😠': ['angry', 'mad', 'upset'],
      '💀': ['skull', 'dead', 'death', 'dying'],
      '💩': ['poop', 'shit', 'crap', 'funny'],
      '🤡': ['clown', 'funny', 'joke', 'silly'],
      '👻': ['ghost', 'spooky', 'halloween', 'boo'],
      '👽': ['alien', 'ufo', 'space'],
      '🤖': ['robot', 'ai', 'bot', 'tech'],

      // Hands & Gestures
      '👋': ['wave', 'hello', 'hi', 'bye'],
      '👍': ['thumbs up', 'like', 'yes', 'good', 'approve'],
      '👎': ['thumbs down', 'dislike', 'no', 'bad'],
      '👏': ['clap', 'applause', 'praise', 'bravo'],
      '🙌': ['raise hands', 'celebrate', 'yay', 'praise'],
      '🙏': ['pray', 'please', 'thanks', 'hope', 'namaste'],
      '💪': ['muscle', 'strong', 'flex', 'strength'],
      '👀': ['eyes', 'look', 'watch', 'see'],
      '❤️': ['heart', 'love', 'red'],
      '💔': ['broken heart', 'heartbreak', 'sad', 'breakup'],
      '✨': ['sparkle', 'stars', 'shine', 'magic'],
      '🔥': ['fire', 'hot', 'flame', 'lit'],
      '💯': ['100', 'hundred', 'perfect', 'full'],
      '✅': ['check', 'yes', 'done', 'correct'],
      '❌': ['x', 'no', 'wrong', 'cross'],

      // Animals
      '🐶': ['dog', 'puppy', 'pet'],
      '🐱': ['cat', 'kitty', 'pet'],
      '🐭': ['mouse', 'rat'],
      '🐰': ['rabbit', 'bunny'],
      '🦊': ['fox'],
      '🐻': ['bear'],
      '🐼': ['panda', 'bear'],
      '🐨': ['koala', 'bear'],
      '🐯': ['tiger', 'cat'],
      '🦁': ['lion', 'cat'],
      '🐮': ['cow'],
      '🐷': ['pig'],
      '🐸': ['frog'],
      '🐵': ['monkey'],
      '🙈': ['monkey', 'see no evil', 'hide'],
      '🙉': ['monkey', 'hear no evil'],
      '🙊': ['monkey', 'speak no evil'],
      '🐔': ['chicken', 'rooster'],
      '🐧': ['penguin'],
      '🐦': ['bird'],
      '🦆': ['duck'],
      '🦉': ['owl'],
      '🐝': ['bee', 'honey'],
      '🦋': ['butterfly'],
      '🐢': ['turtle'],
      '🐍': ['snake'],
      '🦖': ['dinosaur', 't-rex', 'dino'],
      '🐠': ['fish', 'tropical'],
      '🐬': ['dolphin'],
      '🐳': ['whale'],
      '🦈': ['shark'],

      // Nature
      '🌲': ['tree', 'evergreen', 'pine'],
      '🌳': ['tree'],
      '🌴': ['palm tree', 'tropical'],
      '🌵': ['cactus', 'desert'],
      '🌺': ['flower', 'hibiscus'],
      '🌸': ['flower', 'cherry blossom'],
      '🌼': ['flower', 'blossom'],
      '🌻': ['sunflower', 'flower'],
      '🌹': ['rose', 'flower', 'love'],
      '🍀': ['clover', 'lucky', 'four leaf'],
      '🌙': ['moon', 'night', 'crescent'],
      '⭐': ['star'],
      '🌟': ['star', 'sparkle', 'glowing'],
      '☀️': ['sun', 'sunny', 'weather'],
      '⛅': ['cloud', 'sun', 'weather', 'partly cloudy'],
      '🌧️': ['rain', 'weather', 'rainy'],
      '⛈️': ['storm', 'thunder', 'lightning'],
      '❄️': ['snow', 'cold', 'winter'],
      '🌈': ['rainbow', 'colorful'],

      // Food
      '🍎': ['apple', 'fruit', 'red'],
      '🍊': ['orange', 'fruit', 'citrus'],
      '🍋': ['lemon', 'fruit', 'citrus', 'sour'],
      '🍌': ['banana', 'fruit'],
      '🍉': ['watermelon', 'fruit'],
      '🍇': ['grapes', 'fruit'],
      '🍓': ['strawberry', 'fruit', 'berry'],
      '🥑': ['avocado', 'fruit'],
      '🥦': ['broccoli', 'vegetable'],
      '🌽': ['corn', 'vegetable'],
      '🥕': ['carrot', 'vegetable'],
      '🍕': ['pizza', 'food', 'italian'],
      '🍔': ['burger', 'hamburger', 'food'],
      '🍟': ['fries', 'french fries', 'food'],
      '🌭': ['hot dog', 'food'],
      '🍿': ['popcorn', 'snack', 'movie'],
      '🍩': ['donut', 'doughnut', 'sweet'],
      '🍪': ['cookie', 'sweet'],
      '🎂': ['cake', 'birthday', 'celebration'],
      '🍰': ['cake', 'dessert', 'sweet'],
      '🍫': ['chocolate', 'candy', 'sweet'],
      '☕': ['coffee', 'drink', 'hot'],
      '🍵': ['tea', 'drink', 'hot'],
      '🍺': ['beer', 'drink', 'alcohol'],
      '🍷': ['wine', 'drink', 'alcohol'],

      // Activities
      '⚽': ['soccer', 'football', 'ball', 'sport'],
      '🏀': ['basketball', 'ball', 'sport'],
      '🏈': ['football', 'american football', 'sport'],
      '⚾': ['baseball', 'ball', 'sport'],
      '🎾': ['tennis', 'ball', 'sport'],
      '🏐': ['volleyball', 'ball', 'sport'],
      '🏉': ['rugby', 'ball', 'sport'],
      '🏆': ['trophy', 'win', 'award', 'champion'],
      '🥇': ['gold', 'medal', 'first', 'win'],
      '🥈': ['silver', 'medal', 'second'],
      '🥉': ['bronze', 'medal', 'third'],
      '🏅': ['medal', 'award', 'win'],
      '🎮': ['game', 'video game', 'controller', 'gaming'],
      '🎯': ['target', 'dart', 'bullseye', 'goal'],
      '🎨': ['art', 'paint', 'palette', 'creative'],
      '🎭': ['theater', 'drama', 'masks', 'acting'],
      '🎪': ['circus', 'tent'],
      '🎬': ['movie', 'film', 'cinema', 'clapper'],
      '🎤': ['microphone', 'sing', 'music'],
      '🎧': ['headphones', 'music', 'listen'],
      '🎸': ['guitar', 'music', 'rock'],
      '🎹': ['piano', 'music', 'keyboard'],
      '🏊': ['swim', 'swimming', 'pool', 'sport'],
      '🏄': ['surf', 'surfing', 'wave', 'sport'],
      '🚣': ['row', 'rowing', 'boat', 'sport'],
      '🧗': ['climb', 'climbing', 'rock', 'sport'],
      '🚵': ['mountain bike', 'biking', 'cycling', 'sport'],
      '🚴': ['bike', 'bicycle', 'cycling', 'ride', 'sport'],
      '🧘': ['yoga', 'meditation', 'zen', 'relax'],
      '🏇': ['horse', 'racing', 'equestrian', 'sport'],

      // Travel & Vehicles
      '🚗': ['car', 'auto', 'automobile', 'vehicle'],
      '🚕': ['taxi', 'cab', 'car'],
      '🚙': ['suv', 'car', 'vehicle'],
      '🚌': ['bus', 'transit'],
      '🏎️': ['race car', 'racing', 'fast', 'speed'],
      '🚓': ['police', 'cop', 'car'],
      '🚑': ['ambulance', 'emergency', 'medical'],
      '🚒': ['fire truck', 'fire', 'emergency'],
      '🚚': ['truck', 'delivery', 'lorry'],
      '🚛': ['truck', 'semi', 'lorry'],
      '🛴': ['scooter', 'kick scooter'],
      '🚲': ['bicycle', 'bike', 'cycle', 'pedal'],
      '🛵': ['scooter', 'motor scooter', 'moped'],
      '🏍️': ['motorcycle', 'motorbike', 'bike'],
      '✈️': ['airplane', 'plane', 'flight', 'travel'],
      '🚀': ['rocket', 'space', 'launch'],
      '🛸': ['ufo', 'alien', 'spaceship'],
      '🚁': ['helicopter', 'chopper'],
      '🛶': ['canoe', 'boat', 'kayak'],
      '⛵': ['sailboat', 'boat', 'sailing'],
      '🚤': ['speedboat', 'boat', 'fast'],
      '🛥️': ['yacht', 'boat', 'luxury'],
      '🚢': ['ship', 'cruise', 'boat'],
      '🏰': ['castle', 'fortress', 'medieval'],
      '🏯': ['castle', 'japanese', 'pagoda'],
      '🗿': ['moai', 'easter island', 'statue'],
      '🗽': ['statue of liberty', 'liberty', 'new york'],
      '🗼': ['tokyo tower', 'tower', 'landmark'],
      '🎡': ['ferris wheel', 'carnival', 'fair'],
      '🎢': ['roller coaster', 'thrill', 'ride'],
      '🎠': ['carousel', 'merry go round', 'horse'],
      '🏖️': ['beach', 'sand', 'ocean', 'vacation'],
      '🏝️': ['island', 'tropical', 'beach'],
      '🏔️': ['mountain', 'snow', 'peak', 'hiking'],
      '⛰️': ['mountain', 'hiking', 'climbing'],
      '🌋': ['volcano', 'eruption', 'lava'],
      '🗻': ['mount fuji', 'mountain', 'japan'],
      '🏕️': ['camping', 'camp', 'tent', 'outdoor'],
      '⛺': ['tent', 'camping', 'outdoor'],
      '🏠': ['house', 'home', 'building'],
      '🏡': ['house', 'home', 'garden'],
      '🏢': ['office', 'building', 'work'],
      '🏬': ['department store', 'shopping', 'mall'],
      '🏥': ['hospital', 'medical', 'doctor'],
      '🏦': ['bank', 'money', 'finance'],
      '🏨': ['hotel', 'motel', 'lodging'],
      '🏪': ['convenience store', 'shop', 'store'],
      '🏫': ['school', 'education', 'learning'],
      '💒': ['wedding', 'chapel', 'marriage'],
      '⛪': ['church', 'chapel', 'religion'],
      '🕌': ['mosque', 'islam', 'religion'],

      // Objects
      '📱': ['phone', 'mobile', 'cell', 'smartphone'],
      '💻': ['laptop', 'computer', 'pc'],
      '⌚': ['watch', 'time', 'clock'],
      '📷': ['camera', 'photo', 'picture'],
      '💡': ['light bulb', 'idea', 'bright'],
      '🔋': ['battery', 'power', 'charge'],
      '💰': ['money', 'cash', 'bag', 'rich'],
      '💵': ['dollar', 'money', 'cash', 'bill'],
      '💳': ['credit card', 'card', 'payment'],
      '🎁': ['gift', 'present', 'box', 'surprise'],
      '🎈': ['balloon', 'party', 'celebrate'],
      '🎉': ['party', 'celebrate', 'confetti'],
      '📧': ['email', 'mail', 'message'],
      '🔑': ['key', 'lock', 'unlock'],
      '🔒': ['lock', 'secure', 'private'],
      '🔓': ['unlock', 'open'],

      // Symbols
      '✔️': ['check', 'yes', 'correct', 'done'],
      '🔴': ['red', 'circle'],
      '🟠': ['orange', 'circle'],
      '🟡': ['yellow', 'circle'],
      '🟢': ['green', 'circle'],
      '🔵': ['blue', 'circle'],
      '🟣': ['purple', 'circle'],
      '⚫': ['black', 'circle'],
      '⚪': ['white', 'circle'],
      '⬆️': ['up', 'arrow', 'north'],
      '⬇️': ['down', 'arrow', 'south'],
      '⬅️': ['left', 'arrow', 'west'],
      '➡️': ['right', 'arrow', 'east'],
      '🔄': ['refresh', 'reload', 'repeat', 'cycle'],
    }

    return keywordMap[emoji] || []
  }

  // Filter emojis based on search
  const filteredEmojis = useMemo(() => {
    if (!emojiSearch.trim()) {
      return POPULAR_EMOJIS
    }

    const searchLower = emojiSearch.toLowerCase().trim()
    return POPULAR_EMOJIS.filter(emoji => {
      const keywords = getEmojiKeywords(emoji)
      return keywords.some(keyword => keyword.includes(searchLower))
    })
  }, [emojiSearch])

  const renderPostTree = (node: PostNode, depth: number = 0, isLastSibling: boolean = false) => {
    return (
      <Box key={node.id} className={!isLastSibling ? `has-next-sibling` : ''}>
        <DiscussionPost
          post={node}
          depth={depth}
          groupId={groupId}
          discussionId={discussionId}
          isHighlighted={highlightedPostId === node.id}
          canReply={canReply}
          isModerator={isModerator}
          onReply={handleCreateReply}
          onEdit={handleEditPost}
          onDelete={handleDeletePost}
          onFlag={handleFlagPost}
          onHide={handleHidePost}
          onUnhide={handleUnhidePost}
          isSubmitting={
            createPostMutation.isPending ||
            updatePostMutation.isPending ||
            deletePostMutation.isPending
          }
        />
        {node.children.map((child, index) => renderPostTree(child, depth + 1, index === node.children.length - 1))}
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load discussion posts: {error instanceof Error ? error.message : 'Unknown error'}
      </Alert>
    )
  }

  const isSubmitting =
    createPostMutation.isPending ||
    updatePostMutation.isPending ||
    deletePostMutation.isPending ||
    uploadingImage

  return (
    <Box className="discussion-container" sx={{ width: '100%' }}>
      {/* Posts List */}
      {isLoading ? (
        <Box className="discussion-loading">
          <CircularProgress />
        </Box>
      ) : postTree.length === 0 ? (
        <Paper elevation={0} className="discussion-empty">
          <Typography variant="body2" color="text.secondary">
            No posts yet. Be the first to comment!
          </Typography>
        </Paper>
      ) : (
        <Box>{postTree.map((node, index) => renderPostTree(node, 0, index === postTree.length - 1))}</Box>
      )}

      {/* New Post Input - Only show if canReply is true */}
      {canReply && (
      <Paper
        elevation={0}
        className="discussion-reply-box"
        sx={{
          width: '100%',
          boxSizing: 'border-box',
          border: isDragOver ? '2px dashed #1976d2' : 'none',
          backgroundColor: isDragOver ? '#e3f2fd' : 'transparent',
          transition: 'all 0.2s',
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Image Preview */}
        {imagePreview && (
          <Box sx={{ mb: 1, position: 'relative', display: 'inline-block' }}>
            <Box
              component="img"
              src={imagePreview}
              alt="Preview"
              sx={{
                maxWidth: 200,
                maxHeight: 200,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            />
            <IconButton
              size="small"
              onClick={handleRemoveImage}
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                bgcolor: 'background.paper',
                '&:hover': { bgcolor: 'background.paper' },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <TextField
              ref={textFieldRef}
              fullWidth
              multiline={isReplyFocused}
              minRows={isReplyFocused ? 2 : 1}
              maxRows={isReplyFocused ? 6 : 1}
              placeholder={isDragOver ? "Drop image here..." : "Write a reply..."}
              value={newPostContent}
              onChange={(e) => setNewPostContent(e.target.value)}
              onPaste={handlePaste}
              onKeyDown={(e) => {
                // Submit on Enter (without Shift), allow Shift+Enter for new line
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  if (newPostContent.trim() && !isSubmitting) {
                    handleCreateTopLevelPost()
                  }
                }
              }}
              onFocus={() => setIsReplyFocused(true)}
              onBlur={() => {
                if (!newPostContent.trim() && !selectedImage) {
                  setIsReplyFocused(false)
                }
              }}
              disabled={isSubmitting}
              size="small"
              className="discussion-reply-input"
            />

            {/* Helper icons row */}
            <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {/* Camera icon */}
              <Box
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: isSubmitting || uploadingImage || !!selectedImage ? 'default' : 'pointer',
                  opacity: isSubmitting || uploadingImage || !!selectedImage ? 0.5 : 1,
                  '&:hover': {
                    opacity: isSubmitting || uploadingImage || !!selectedImage ? 0.5 : 0.8,
                  },
                }}
              >
                <CameraIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  Paste or drag images
                </Typography>
              </Box>

              {/* Emoji icon */}
              <Box
                onClick={handleEmojiClick}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: isSubmitting ? 'default' : 'pointer',
                  opacity: isSubmitting ? 0.5 : 1,
                  '&:hover': {
                    opacity: isSubmitting ? 0.5 : 0.8,
                  },
                }}
              >
                <EmojiIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  Add emoji
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={handleImageSelect}
            style={{ display: 'none' }}
          />

          <Button
            size="small"
            variant="contained"
            onClick={handleCreateTopLevelPost}
            disabled={isSubmitting || !newPostContent.trim()}
            className="discussion-reply-button"
          >
            {uploadingImage ? <CircularProgress size={20} /> : 'Reply'}
          </Button>
        </Box>
      </Paper>
      )}

      {/* Emoji Picker Popover */}
      <Popover
        open={Boolean(emojiAnchorEl)}
        anchorEl={emojiAnchorEl}
        onClose={handleEmojiClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box sx={{ width: 350, p: 1.5 }}>
          {/* Search Input */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search emojis..."
            value={emojiSearch}
            onChange={(e) => setEmojiSearch(e.target.value)}
            sx={{ mb: 1.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">🔍</InputAdornment>
              ),
            }}
          />

          {/* Emoji Grid - Popular Emojis */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(9, 32px)',
              gap: 0.5,
              maxHeight: 320,
              overflowY: 'auto',
              overflowX: 'hidden',
              justifyContent: 'center',
            }}
          >
            {filteredEmojis.map((emoji, index) => (
              <Button
                key={`${emoji}-${index}`}
                onClick={() => {
                  handleEmojiSelect(emoji)
                  handleEmojiClose()
                }}
                sx={{
                  minWidth: 'auto',
                  width: 32,
                  height: 32,
                  p: 0,
                  fontSize: '1.25rem',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    transform: 'scale(1.2)',
                  },
                  transition: 'all 0.1s',
                }}
              >
                {emoji}
              </Button>
            ))}
          </Box>
        </Box>
      </Popover>
    </Box>
  )
}
